import { parse } from 'csv-parse/sync'
import { stringify } from 'csv-stringify/sync'
import { z } from 'zod'
import { postDebtorService } from '@/shared/services/debtorServices'
import type { Company } from '@/shared/models/companyModel'
import type { Currency } from '@/shared/models/moneyModel'
import { countryCodes, identifierTypes } from '@/shared/models/debtorModels'
import { postCreditService } from '@/modules/credits/services/creditsServices'

export const BulkDebtorRowSchema = z.object({
    name: z.string().min(1, 'Name is required'),
    identifierType: z.enum(identifierTypes, {
        errorMap: () => ({ message: 'Invalid identifierType' })
    }),
    identifierValue: z.string().min(1, 'identifierValue is required'),
    identifierCountry: z.enum(countryCodes, {
        errorMap: () => ({ message: 'Invalid identifierCountry' })
    }),
    addressFirst: z.string().min(1, 'Address line is required'),
    addressCity: z.string().min(1, 'City is required'),
    addressPostal: z.string().min(1, 'Postal code is required'),
    addressCountry: z.enum(countryCodes, {
        errorMap: () => ({ message: 'Invalid addressCountry' })
    }),
    contactEmail: z.string().email('Invalid contact email'),
    contactPhoneNumber: z.string().default(''),
    contactFirstName: z.string().optional().default(''),
    contactLastName: z.string().optional().default(''),
    contactPosition: z.string().optional().default('')
})

export type BulkDebtorRow = z.infer<typeof BulkDebtorRowSchema>

export type BulkUploadError = {
    code: 'EMPTY_FILE' | 'MISSING_HEADERS' | 'PARSING_ERROR' | 'UNKNOWN_ERROR'
    details?: {
        missingHeaders?: string[]
        expectedHeaders?: string[]
        csvHeaders?: string[]
    }
    originalError?: unknown
}

export const isBulkUploadError = (obj: unknown): obj is BulkUploadError => {
    if (typeof obj !== 'object' || obj === null) return false

    const maybeError = obj as BulkUploadError

    return (
        typeof maybeError.code === 'string' &&
        ['EMPTY_FILE', 'MISSING_HEADERS', 'PARSING_ERROR', 'UNKNOWN_ERROR'].includes(maybeError.code)
    )
}

type BulkCreateDebtorsOptions = {
    currency?: Currency
    shouldCreateCreditLimit?: boolean
}

type RowResult = BulkDebtorRow & {
    debtorStatus: 'Success' | 'Error'
    debtorError?: string
    creditLimitStatus: 'Created' | 'Skipped' | 'Not requested' | 'Error'
    creditLimitError?: string
    debtorId?: string | null
    creditLimitId?: string | null
}

export async function bulkCreateDebtorsService(
    file: Blob,
    companyId: Company['id'],
    options?: BulkCreateDebtorsOptions
): Promise<Blob> {
    const csvContent = await file.text()
    const records = parse(csvContent, {
        columns: true,
        skip_empty_lines: true,
        trim: true
    }) satisfies Record<string, string>[]

    const shouldCreateCreditLimit = options?.shouldCreateCreditLimit === true

    if (shouldCreateCreditLimit && !options?.currency) {
        throw new Error('Currency is required when shouldCreateCreditLimit is true')
    }

    const getClaimedCurrency = (): Currency => {
        if (!options?.currency) {
            throw new Error('Currency is required when shouldCreateCreditLimit is true')
        }
        return options.currency
    }

    const results: RowResult[] = []

    for (const [, record] of records.entries()) {
        let rowBase: BulkDebtorRow
        let debtorId: string | null = null
        let creditLimitId: string | null = null

        let debtorStatus: RowResult['debtorStatus'] = 'Error'
        let debtorError: string | undefined

        let creditLimitStatus: RowResult['creditLimitStatus'] = shouldCreateCreditLimit ? 'Skipped' : 'Not requested'
        let creditLimitError: string | undefined

        try {
            const parsed = BulkDebtorRowSchema.parse(record)
            rowBase = parsed

            try {
                const debtorResponse = await postDebtorService(
                    {
                        name: parsed.name,
                        identifier: {
                            type: parsed.identifierType,
                            value: parsed.identifierValue,
                            country: parsed.identifierCountry
                        },
                        address: {
                            first: parsed.addressFirst,
                            city: parsed.addressCity,
                            postal: parsed.addressPostal,
                            country: parsed.addressCountry
                        },
                        contacts: [
                            {
                                email: parsed.contactEmail,
                                firstName: parsed.contactFirstName,
                                lastName: parsed.contactLastName,
                                phoneNumber: parsed.contactPhoneNumber,
                                position: parsed.contactPosition
                            }
                        ]
                    },
                    companyId
                )

                debtorId = debtorResponse?.body?.id ?? null
                debtorStatus = 'Success'
            } catch (err) {
                debtorStatus = 'Error'
                debtorError =
                    err instanceof z.ZodError
                        ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                        : err instanceof Error
                          ? err.message
                          : 'Unknown error while creating debtor'
            }

            if (shouldCreateCreditLimit) {
                if (debtorStatus === 'Success' && debtorId) {
                    try {
                        const creditResp = await postCreditService(
                            {
                                claimedAmount: null,
                                claimedCurrency: getClaimedCurrency(),
                                debtorId,
                                debtorIdentifier: {
                                    type: parsed.identifierType,
                                    value: parsed.identifierValue,
                                    country: parsed.identifierCountry
                                }
                            },
                            companyId
                        )

                        creditLimitId = creditResp?.body?.id ?? null
                        creditLimitStatus = 'Created'
                    } catch (err) {
                        creditLimitStatus = 'Error'
                        creditLimitError =
                            err instanceof z.ZodError
                                ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                                : err instanceof Error
                                  ? err.message
                                  : 'Unknown error while creating credit limit'
                    }
                } else {
                    creditLimitStatus = 'Skipped'
                    creditLimitError = 'Skipped because debtor creation failed'
                }
            }

            results.push({
                ...rowBase,
                debtorStatus,
                debtorError,
                creditLimitStatus,
                creditLimitError,
                debtorId,
                creditLimitId
            })
        } catch (err) {
            const message =
                err instanceof z.ZodError
                    ? err.errors.map((e) => `${e.path.join('.')}: ${e.message}`).join('; ')
                    : err instanceof Error
                      ? err.message
                      : 'Unknown error'

            results.push({
                ...(record as BulkDebtorRow),
                debtorStatus: 'Error',
                debtorError: message,
                creditLimitStatus: shouldCreateCreditLimit ? 'Skipped' : 'Not requested',
                creditLimitError: shouldCreateCreditLimit ? 'Skipped because row validation failed' : undefined,
                debtorId: null,
                creditLimitId: null
            })
        }
    }

    const outputCsv = stringify(results, {
        header: true,
        columns: [
            ...Object.keys(BulkDebtorRowSchema.shape),
            'debtorStatus',
            'debtorError',
            'creditLimitStatus',
            'creditLimitError',
            'debtorId',
            'creditLimitId'
        ]
    })

    return new Blob([outputCsv], { type: 'text/csv;charset=utf-8' })
}
